import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { State } from 'redux/initialState';
import { store } from 'redux/store';

import { Council, Society } from 'types';

import sizes from 'constants/sizes';
import signatureTypes from 'constants/signatureTypes';
import { useTranslate } from 'hooks/useTranslate';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addAlert } from 'modules/_shared/redux/alertActions';
import TemplateViewer from 'modules/_shared/components/Modals/TemplateViewer';
import DocumentViewer from 'modules/_shared/components/Modals/DocumentViewer';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import { getDocument } from 'modules/documents/redux/documentActions';
import { downloadTemplateDocument } from 'modules/_shared/components/DocumentsColumn/utils/downloadTemplateDocument';

import useGetDocumentData from 'modules/administratives/pages/CouncilDetails/hooks/useGetDocumentData';
import EnvelopeDetailsModal from 'modules/envelopes/modals/EnvelopeDetailsModal';
import councilTemplateTypes from 'modules/administratives/pages/CouncilDetails/constants/councilTemplateTypes';
import RequestSignatureModal from 'modules/administratives/modals/RequestSignatureModal';
import EditCouncilActEditor from 'modules/administratives/modals/EditCouncilActEditor';
import EditCouncilAnnouncement from 'modules/administratives/modals/EditCouncilAnnouncement';
import EditCouncilActCertificate from 'modules/administratives/modals/EditCouncilActCertificate';
import EditCouncilCertificateEditor from 'modules/administratives/modals/EditCouncilCertificateEditor';
import EditCouncilAnnouncementEditor from 'modules/administratives/modals/EditCouncilAnnouncementEditor';
import EditCouncilAnnouncementUpload from 'modules/administratives/modals/EditCouncilAnnouncementUpload';
import fileTypes from 'constants/fileTypes';
import downloadDocument from 'utils/downloadDocument';

export type DocumentType = 'EMAIL' | 'PDF' | 'TEMPLATE' | 'OTHER';

export type DocumentsGroupItem = {
  name: string;
  size: number | null;
  date: string;
  description: string;
  content: any;
  contentType: DocumentType;
  isPublic?: boolean;
};

export enum ContentTypes {
  EMAIL = 'EMAIL', // eslint-disable-line no-unused-vars
  PDF = 'PDF', // eslint-disable-line no-unused-vars
  TEMPLATE = 'TEMPLATE', // eslint-disable-line no-unused-vars
  OTHER = 'OTHER', // eslint-disable-line no-unused-vars
}

type UseCouncilMenuOptionsProps = {
  council: Council;
  society?: Society;
  isAdminView: boolean;
};

const useCouncilMenuOptions = ({
  council,
  isAdminView,
}: UseCouncilMenuOptionsProps) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  );

  const hasSentAnnouncement = !!council?.announcementCommunication;
  const hasSentAct = !!council?.actSignature?.envelopeId;
  const hasSentCertificate = !!council?.actCertificateSignature?.envelopeId;

  const actTemplateData = useGetDocumentData({
    documentId: council?.actDocument ?? null,
    templateId: council?.actTemplate ?? null,
  });

  const certificateTemplateData = useGetDocumentData({
    documentId: council?.actCertificateDocument ?? null,
    templateId: council?.actCertificateTemplate ?? null,
  });

  // Action handlers
  const handleEditAnnouncement = useCallback(() => {
    dispatch(
      setModal(<EditCouncilAnnouncement size={sizes.XXL} council={council} />)
    );
  }, [dispatch, council]);

  const handleEditAct = useCallback(() => {
    dispatch(
      setModal(<EditCouncilActCertificate size={sizes.XXL} council={council} />)
    );
  }, [dispatch, council]);

  const handleEditSignatures = useCallback(() => {
    // Empty implementation
  }, []);

  const handleRequestActSignature = useCallback(() => {
    if (!council?._id || !actTemplateData?.data.document) {
      return;
    }

    dispatch(
      setModal(
        <RequestSignatureModal
          councilId={council._id}
          document={actTemplateData.data.document}
          templateType={councilTemplateTypes.ACT}
          signatureType={signatureTypes.COUNCIL_ACT}
        />
      )
    );
  }, [dispatch, council?._id, actTemplateData?.data?.document]);

  const handleRequestCertificateSignature = useCallback(() => {
    if (!council?._id || !certificateTemplateData?.data.document) {
      return;
    }

    dispatch(
      setModal(
        <RequestSignatureModal
          councilId={council._id}
          document={certificateTemplateData.data.document}
          templateType={councilTemplateTypes.CERTIFICATION}
          signatureType={signatureTypes.COUNCIL_CERTIFICATE}
        />
      )
    );
  }, [dispatch, council?._id, certificateTemplateData?.data?.document]);

  const handleSeeActSignatureStatus = useCallback(() => {
    const envelopeId = council?.actSignature?.envelopeId;
    if (!envelopeId) {
      return;
    }

    dispatch(
      setModal(
        <EnvelopeDetailsModal
          title={t('CouncilActSignatureStatus')}
          envelopeId={envelopeId}
          size={sizes.XXL}
        />
      )
    );
  }, [dispatch, council?.actSignature?.envelopeId, t]);

  const handleSeeCertificateSignatureStatus = useCallback(() => {
    const envelopeId = council?.actCertificateSignature?.envelopeId;
    if (!envelopeId) {
      return;
    }

    dispatch(
      setModal(
        <EnvelopeDetailsModal
          title={t('CouncilActCertificateSignatureStatus')}
          envelopeId={envelopeId}
          size={sizes.XXL}
        />
      )
    );
  }, [dispatch, council?.actCertificateSignature?.envelopeId, t]);

  const handleClickDocument = useCallback(
    (item: DocumentsGroupItem) => {
      if (!item.content || !item.isPublic) return;

      if (item.contentType === ContentTypes.PDF && item.content._id) {
        dispatch(
          setModal(<DocumentViewer document={item.content} size={sizes.XL} />)
        );
      }

      if (
        item.contentType === ContentTypes.TEMPLATE ||
        item.contentType === ContentTypes.EMAIL
      ) {
        dispatch(
          setModal(
            <TemplateViewer
              template={item.content}
              contentType={item.contentType}
              size={sizes.XL}
            />
          )
        );
      }
    },
    [dispatch]
  );

  const handleEditAnnouncementEditor = useCallback(() => {
    dispatch(
      setModal(
        <EditCouncilAnnouncementEditor size={sizes.LG} council={council} />
      )
    );
  }, [dispatch, council]);

  const handleEditAnnouncementUpload = useCallback(() => {
    dispatch(
      setModal(
        <EditCouncilAnnouncementUpload size={sizes.LG} council={council} />
      )
    );
  }, [dispatch, council]);

  const handleEditActEditor = useCallback(() => {
    dispatch(
      setModal(<EditCouncilActEditor size={sizes.LG} council={council} />)
    );
  }, [dispatch, council]);

  const handleEditCertificateEditor = useCallback(() => {
    dispatch(
      setModal(
        <EditCouncilCertificateEditor size={sizes.LG} council={council} />
      )
    );
  }, [dispatch, council]);

  const handleDownloadDocument = useCallback(
    async (document: { _id: string; name: string }) => {
      try {
        if (!document._id) return;
        setIsLoading(true);
        setIsError(false);

        const documentData = await store.dispatch(getDocument(document._id));

        if (!documentData) return;

        if (documentData?.fileType === fileTypes.TEMPLATE) {
          await downloadTemplateDocument({
            document: documentData,
            tagsObject: { society: actualSociety, council },
            isLandscape: false,
          });
        }

        if (documentData?.fileType === fileTypes.PDF) {
          await downloadDocument({
            documentId: documentData._id,
            documentName: documentData.name,
            multiple: false,
            documents: [],
          });
        }

        setIsLoading(false);
      } catch {
        setIsLoading(false);
        setIsError(true);
        dispatch(addAlert(alertBodyTypes.ERROR_DOWNLOADING_DOCUMENT));
      }
    },
    [dispatch, actualSociety, council]
  );

  // Menu options generators
  const getAnnouncementMenuOptions = useCallback(
    (item: DocumentsGroupItem, index: number) => {
      if (!council) return [];

      const isFirstItem = index === 0;
      const isSecondItem = index === 1; // CouncilAgenda

      const menuOptions = [
        <Dropdown.Item key="see" onClick={() => handleClickDocument(item)}>
          <em className="icon ni ni-eye mr-1" />
          {t('See')}
        </Dropdown.Item>,
      ];

      // Add download option for 2nd item (CouncilAgenda)
      if (isSecondItem) {
        menuOptions.push(
          <Dropdown.Item
            key="download"
            onClick={() =>
              handleDownloadDocument({
                _id: council?.announcementDocument || '',
                name: item?.content?.name || '',
              })
            }
          >
            <em className="icon ni ni-download mr-1" />
            {t('Download')}
          </Dropdown.Item>
        );
      }

      // Only show Edit/Replace options in Admin view
      if (isAdminView) {
        menuOptions.push(
          <Dropdown.Item
            key="edit-replace"
            onClick={
              isFirstItem
                ? handleEditAnnouncementEditor
                : handleEditAnnouncementUpload
            }
            disabled={isFirstItem && hasSentAnnouncement}
          >
            <em className="icon ni ni-files mr-1" />
            {isFirstItem ? t('Edit') : t('Replace')}
          </Dropdown.Item>
        );
      }

      return menuOptions;
    },
    [
      t,
      handleClickDocument,
      handleDownloadDocument,
      council,
      isAdminView,
      handleEditAnnouncementEditor,
      handleEditAnnouncementUpload,
      hasSentAnnouncement,
    ]
  );

  const getActMenuOptions = useCallback(
    (item: DocumentsGroupItem, index: number) => {
      if (!council) return [];

      const isFirstItem = index === 0;

      const menuOptions = [
        <Dropdown.Item key="see" onClick={() => handleClickDocument(item)}>
          <em className="icon ni ni-eye mr-1" />
          {t('See')}
        </Dropdown.Item>,
      ];

      // Add download option for both Act and Certificate items
      menuOptions.push(
        <Dropdown.Item
          key="download"
          onClick={() =>
            handleDownloadDocument(
              isFirstItem
                ? {
                    _id: council?.actDocument || '',
                    name: item?.content?.name || '',
                  }
                : {
                    _id: council?.actCertificateDocument || '',
                    name: item?.content?.name || '',
                  }
            )
          }
        >
          <em className="icon ni ni-download mr-1" />
          {t('Download')}
        </Dropdown.Item>
      );

      // Only show Replace option in Admin view
      if (isAdminView) {
        menuOptions.push(
          <Dropdown.Item
            key="replace"
            onClick={
              isFirstItem ? handleEditActEditor : handleEditCertificateEditor
            }
            disabled={
              (isFirstItem && hasSentAct) ||
              (!isFirstItem && hasSentCertificate)
            }
          >
            <em className="icon ni ni-files mr-1" />
            {t('Replace')}
          </Dropdown.Item>
        );
      }

      return menuOptions;
    },
    [
      t,
      handleClickDocument,
      handleDownloadDocument,
      council,
      isAdminView,
      handleEditActEditor,
      handleEditCertificateEditor,
      hasSentAct,
      hasSentCertificate,
    ]
  );

  const getSignaturesMenuOptions = useCallback(
    (item: DocumentsGroupItem, index: number) => {
      const isFirstItem = index === 0;
      const hasEnvelope = isFirstItem
        ? !!council?.actSignature?.envelopeId
        : !!council?.actCertificateSignature?.envelopeId;

      const menuOptions = [];

      // Only show signature-related options in Admin view
      if (isAdminView) {
        menuOptions.push(
          <Dropdown.Item
            key="request-signature"
            onClick={
              isFirstItem
                ? handleRequestActSignature
                : handleRequestCertificateSignature
            }
            disabled={
              (isFirstItem && hasSentAct) ||
              (!isFirstItem && hasSentCertificate)
            }
          >
            <em className="icon ni ni-edit-alt mr-1" />
            {t('RequestSignature')}
          </Dropdown.Item>,
          <Dropdown.Item
            key="see-status"
            onClick={
              isFirstItem
                ? handleSeeActSignatureStatus
                : handleSeeCertificateSignatureStatus
            }
            disabled={!hasEnvelope}
          >
            <em className="icon ni ni-eye mr-1" />
            {t('SeeStatus')}
          </Dropdown.Item>,
          <Dropdown.Item key="cancel" onClick={() => {}} disabled>
            <em className="icon ni ni-cross mr-1" />
            {t('Cancel')}
          </Dropdown.Item>
        );
      }

      return menuOptions;
    },
    [
      isAdminView,
      hasSentAct,
      hasSentCertificate,
      handleRequestActSignature,
      handleRequestCertificateSignature,
      t,
      handleSeeActSignatureStatus,
      handleSeeCertificateSignatureStatus,
      council?.actSignature?.envelopeId,
      council?.actCertificateSignature?.envelopeId,
    ]
  );

  return {
    data: {
      getAnnouncementMenuOptions,
      getActMenuOptions,
      getSignaturesMenuOptions,
    },
    actions: {
      handleEditAnnouncement,
      handleEditAct,
      handleEditSignatures,
      handleClickDocument,
    },
    isLoading,
    isError,
  };
};

export default useCouncilMenuOptions;
